import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection, Canvas, config} from '@postermywall/fabricjs-2';
import {isWindows} from 'react-device-detect';
import {isTouchDevice} from '@Utils/browser.util';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {CanvasZoomSource} from '@Libraries/ga-events';
import {getZoomDirection, trackCanvasZoom} from '@Libraries/poster-editor-tracking.library';
import {activateMobilePageSelectionMode, exitMobilePageSelectionMode, transitionToMobilePageSelectionMode} from '@PosterWhiteboard/libraries/mobile-page-selection-mode.library';
import {isMovementLockedBody} from '@PosterWhiteboard/libraries/movement-lock.library';
import {PageSwipeManager} from '@PosterWhiteboard/poster/page-swipe-manager.class';
import {CROP_SURFACE_ATTRIBUTE} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';

interface PointerPoint {
  clientX: number;
  clientY: number;
  pointerId: number;
}

interface GestureState {
  pointers: Map<number, PointerPoint>;
  initialScale: number;
  currentGestureScale: number;
  initialDistance: number;
  lastPanX: number;
  lastPanY: number;
  isPanning: boolean;
  isZooming: boolean;
  wasSelectableDisabled: boolean;
  cumulativePanDeltaX: number;
  cumulativePanDeltaY: number;
  isPageSwiping: boolean;
  isPointerDownOnSelectedItem: boolean;
}

const PAN_THRESHOLD_PX = 10;
const ZOOM_THRESHOLD = 0.01;
const RESTORE_SELECTABLE_DELAY_MS = 150;

const INTERACTIVE_CONTROL_SELECTOR = `.rc-slider, [${CROP_SURFACE_ATTRIBUTE}]`;

export class PosterGestures {
  private poster: Poster;
  private fabricObjectsOriginalSelectableMap = new Map<FabricObject, boolean>();
  private restoreSelectableTimeoutId?: number;
  private gestureState: GestureState | null = null;
  private whiteboardContainerElement?: HTMLElement;
  private swipeManager?: PageSwipeManager;
  private readonly handlePointerDown: (event: PointerEvent) => void;
  private readonly handlePointerMove: (event: PointerEvent) => void;
  private readonly handlePointerUp: (event: PointerEvent) => void;

  constructor(poster: Poster) {
    this.poster = poster;
    this.handlePointerDown = this.handlePointerDownInternal.bind(this);
    this.handlePointerMove = this.handlePointerMoveInternal.bind(this);
    this.handlePointerUp = this.handlePointerUpInternal.bind(this);
  }

  public static shouldEnableGestures(): boolean {
    if (isWindows) {
      return false;
    }

    return isTouchDevice();
  }

  public dispose(): void {
    if (this.restoreSelectableTimeoutId) {
      window.clearTimeout(this.restoreSelectableTimeoutId);
      this.restoreSelectableTimeoutId = undefined;
    }
    if (this.whiteboardContainerElement) {
      this.whiteboardContainerElement.removeEventListener('pointerdown', this.handlePointerDown);
      this.whiteboardContainerElement.removeEventListener('pointermove', this.handlePointerMove);
      this.whiteboardContainerElement.removeEventListener('pointerup', this.handlePointerUp);
      this.whiteboardContainerElement.removeEventListener('pointercancel', this.handlePointerUp);
      this.whiteboardContainerElement.style.touchAction = '';
    }
  }

  private setAllFabricObjectsSelectableToFalse(): void {
    if (this.fabricObjectsOriginalSelectableMap.size > 0) {
      return;
    }

    if (this.restoreSelectableTimeoutId) {
      window.clearTimeout(this.restoreSelectableTimeoutId);
      this.restoreSelectableTimeoutId = undefined;
    }
    this.fabricObjectsOriginalSelectableMap.clear();
    for (const [, page] of Object.entries(this.poster.pages.pagesHashMap)) {
      const fabricObjects = page.fabricCanvas.getObjects();
      for (const fabricObject of fabricObjects) {
        this.fabricObjectsOriginalSelectableMap.set(fabricObject, fabricObject.selectable ?? true);
        fabricObject.set({selectable: false});
      }
    }
  }

  private restoreFabricObjectsOriginalSelectable(): void {
    for (const [fabricObject, originalSelectable] of this.fabricObjectsOriginalSelectableMap.entries()) {
      fabricObject.set({selectable: originalSelectable});
    }
    this.fabricObjectsOriginalSelectableMap.clear();
  }

  public initInteractGestures(whiteboardContainerElement: HTMLElement): void {
    this.whiteboardContainerElement = whiteboardContainerElement;
    this.swipeManager = new PageSwipeManager(this.poster, whiteboardContainerElement);
    whiteboardContainerElement.style.touchAction = 'none';
    // Touch uses single-finger drag for panning, so the fabric drag-marquee is disabled.
    // It is desktop-only — leaving it on would start a marquee over the (activeOn 'up')
    // background on the first few px of a pan before the pan threshold takes over.
    config.disableGroupSelector = true;
    this.attachPointerEventListeners(whiteboardContainerElement);
  }

  private attachPointerEventListeners(element: HTMLElement): void {
    element.addEventListener('pointerdown', this.handlePointerDown);
    element.addEventListener('pointermove', this.handlePointerMove);
    element.addEventListener('pointerup', this.handlePointerUp);
    element.addEventListener('pointercancel', this.handlePointerUp);
  }

  private handlePointerDownInternal(event: PointerEvent): void {
    if (!this.whiteboardContainerElement || this.poster.areItemsModifying()) {
      return;
    }

    if (this.isPointerOnInteractiveControl(event)) {
      return;
    }

    this.capturePointer(event);
    this.gestureState ??= this.createInitialGestureState();
    this.addPointerToTracking(event);

    const pointerCount = this.gestureState.pointers.size;

    if (pointerCount === 1) {
      this.gestureState.isPointerDownOnSelectedItem = this.doesCanvasOwnSinglePointer(event);
      this.initializeSinglePointerGesture();
    } else if (pointerCount === 2) {
      this.initializeTwoPointerGesture();
    }
  }

  /**
   * A single finger on a draggable selected item is fabric's own drag, and in an on-canvas edit mode (crop,
   * the Eraser) every single finger on the canvas belongs to that mode - a pan of the picture, a drag of the
   * crop window, a brush stroke or a click beside it. Either way the gesture layer stands aside until a
   * second finger arrives.
   */
  private doesCanvasOwnSinglePointer(event: PointerEvent): boolean {
    return this.poster.isOnCanvasEditModeActive() || this.isPointerOverDraggableSelectedItem(event);
  }

  /**
   * A finger on the selected item is handed to fabric so the item can be dragged. A movement-locked
   * selection (the page background, a locked item) cannot be dragged, so handing it over would
   * leave the finger doing nothing — that press pans the canvas like a press on empty space.
   */
  private isPointerOverDraggableSelectedItem(event: PointerEvent): boolean {
    const page = this.poster.getCurrentPage();
    const canvas = page.fabricCanvas;
    if (!(canvas instanceof Canvas)) {
      return false;
    }
    const activeObject = page.activeSelection.getActiveObject();
    if (!activeObject || isMovementLockedBody(activeObject)) {
      return false;
    }

    const pointer = canvas.getScenePoint(event);
    if (activeObject instanceof ActiveSelection) {
      return activeObject.getObjects().some((obj) => obj.containsPoint(pointer));
    }
    return activeObject.containsPoint(pointer);
  }

  /** A pointer that lands on one of these belongs to that control's own drag, never to a gesture. */
  private isPointerOnInteractiveControl(event: PointerEvent): boolean {
    if (!(event.target instanceof Element)) {
      return false;
    }
    return event.target.closest(INTERACTIVE_CONTROL_SELECTOR) !== null;
  }

  private capturePointer(event: PointerEvent): void {
    if (event.target instanceof Element) {
      event.target.setPointerCapture(event.pointerId);
    }
  }

  private addPointerToTracking(event: PointerEvent): void {
    if (!this.gestureState) {
      return;
    }
    this.gestureState.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
    });
  }

  private initializeSinglePointerGesture(): void {
    if (!this.gestureState) {
      return;
    }
    const pointer = Array.from(this.gestureState.pointers.values())[0];
    this.gestureState.lastPanX = pointer.clientX;
    this.gestureState.lastPanY = pointer.clientY;
    this.gestureState.cumulativePanDeltaX = 0;
    this.gestureState.cumulativePanDeltaY = 0;
    this.gestureState.isPanning = false;
    this.gestureState.wasSelectableDisabled = false;
  }

  private initializeTwoPointerGesture(): void {
    if (!this.gestureState) {
      return;
    }
    this.gestureState.initialScale = this.poster.scaling.scale;
    this.gestureState.currentGestureScale = this.poster.scaling.scale;

    const pointers = Array.from(this.gestureState.pointers.values());
    this.gestureState.initialDistance = this.getPointerDistance(pointers);

    const centroid = this.getPointerCentroid(pointers);
    this.gestureState.lastPanX = centroid.x;
    this.gestureState.lastPanY = centroid.y;

    this.gestureState.isZooming = false;
    this.gestureState.isPanning = false;
    this.gestureState.cumulativePanDeltaX = 0;
    this.gestureState.cumulativePanDeltaY = 0;

    this.setAllFabricObjectsSelectableToFalse();
    this.gestureState.wasSelectableDisabled = true;
  }

  private shouldIgnorePointerMove(): boolean {
    let isDrawingMode = false;
    const canvas = this.poster.getCurrentPage().fabricCanvas;
    if (canvas instanceof Canvas) {
      isDrawingMode = canvas.isDrawingMode;
    }

    // A pointer resting on a draggable selected item defers to fabric's own drag — but only while
    // the gesture is single-pointer: a second finger means pinch-zoom/pan, which must keep working
    // even when the selected item covers the whole page.
    const isSinglePointerGestureOnSelectedItem = !!this.gestureState?.isPointerDownOnSelectedItem && this.gestureState.pointers.size === 1;

    return !this.whiteboardContainerElement || this.poster.areItemsModifying() || isDrawingMode || isSinglePointerGestureOnSelectedItem;
  }

  private handlePointerMoveInternal(event: PointerEvent): void {
    if (!this.gestureState || this.shouldIgnorePointerMove()) {
      return;
    }

    this.updatePointerPosition(event);

    const pointerCount = this.gestureState.pointers.size;
    const pointers = Array.from(this.gestureState.pointers.values());

    if (pointerCount === 1) {
      this.handleOnePointerPan(pointers[0], event);
    } else if (pointerCount === 2) {
      this.handleTwoPointerGesture(pointers, event);
    }
  }

  private updatePointerPosition(event: PointerEvent): void {
    if (!this.gestureState?.pointers.has(event.pointerId)) {
      return;
    }
    this.gestureState.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
    });
  }

  private handlePointerUpInternal(event: PointerEvent): void {
    if (!this.gestureState) {
      return;
    }

    this.releasePointer(event);
    this.gestureState.pointers.delete(event.pointerId);

    const pointerCount = this.gestureState.pointers.size;

    if (pointerCount === 0) {
      this.handleAllPointersReleased();
    } else if (pointerCount === 1) {
      this.handleTransitionToSinglePointer();
    }
  }

  private releasePointer(event: PointerEvent): void {
    if (event.target instanceof Element) {
      event.target.releasePointerCapture(event.pointerId);
    }
  }

  private handleAllPointersReleased(): void {
    if (!this.gestureState) {
      return;
    }

    if (this.gestureState.wasSelectableDisabled) {
      this.scheduleRestoreSelectable();
    }

    if (this.gestureState.isZooming) {
      this.handleZoomEnd();
    }

    if (this.gestureState.isPageSwiping) {
      this.swipeManager?.finalizeSwipe();
    }

    this.gestureState = null;
  }

  private handleTransitionToSinglePointer(): void {
    if (!this.gestureState) {
      return;
    }

    if (this.gestureState.isZooming) {
      this.handleZoomEnd();
    }

    const remainingPointer = Array.from(this.gestureState.pointers.values())[0];
    this.gestureState.lastPanX = remainingPointer.clientX;
    this.gestureState.lastPanY = remainingPointer.clientY;
    this.gestureState.isZooming = false;
    this.gestureState.initialDistance = 0;
    this.gestureState.cumulativePanDeltaX = 0;
    this.gestureState.cumulativePanDeltaY = 0;
    this.gestureState.isPanning = false;
  }

  private handleOnePointerPan(pointer: PointerPoint, event: PointerEvent): void {
    if (!this.whiteboardContainerElement || !this.gestureState) {
      return;
    }

    const deltaX = pointer.clientX - this.gestureState.lastPanX;
    const deltaY = pointer.clientY - this.gestureState.lastPanY;

    this.updateCumulativePanDelta(deltaX, deltaY);

    if (this.shouldStartPanning()) {
      this.startPanning();
    }

    if (this.gestureState.isPanning) {
      if (this.gestureState.isPageSwiping) {
        this.swipeManager?.onSwipeMove(deltaX);
      } else {
        this.performPan(deltaX, deltaY);
      }
      event.preventDefault();
    }

    this.updateLastPanPosition(pointer);
  }

  private updateCumulativePanDelta(deltaX: number, deltaY: number): void {
    if (!this.gestureState) {
      return;
    }
    this.gestureState.cumulativePanDeltaX += Math.abs(deltaX);
    this.gestureState.cumulativePanDeltaY += Math.abs(deltaY);
  }

  private shouldStartPanning(): boolean {
    if (!this.gestureState || this.gestureState.isPanning) {
      return false;
    }
    const totalDelta = this.calculateTotalPanDelta();
    return totalDelta > PAN_THRESHOLD_PX;
  }

  private calculateTotalPanDelta(): number {
    if (!this.gestureState) {
      return 0;
    }
    return Math.sqrt(this.gestureState.cumulativePanDeltaX * this.gestureState.cumulativePanDeltaX + this.gestureState.cumulativePanDeltaY * this.gestureState.cumulativePanDeltaY);
  }

  private startPanning(): void {
    if (!this.gestureState || !this.whiteboardContainerElement) {
      return;
    }
    this.gestureState.isPanning = true;
    this.gestureState.wasSelectableDisabled = true;
    this.setAllFabricObjectsSelectableToFalse();

    if (this.swipeManager?.canSwipe()) {
      this.gestureState.isPageSwiping = true;
      this.swipeManager?.startSwipe();
    }
  }

  private performPan(deltaX: number, deltaY: number): void {
    if (!this.whiteboardContainerElement) {
      return;
    }
    this.whiteboardContainerElement.scroll({
      left: this.whiteboardContainerElement.scrollLeft - deltaX,
      top: this.whiteboardContainerElement.scrollTop - deltaY,
      behavior: 'instant',
    });
    this.poster.refreshAllVisibleCanvases();
  }

  private updateLastPanPosition(pointer: PointerPoint): void {
    if (!this.gestureState) {
      return;
    }
    this.gestureState.lastPanX = pointer.clientX;
    this.gestureState.lastPanY = pointer.clientY;
  }

  private handleTwoPointerGesture(pointers: PointerPoint[], event: PointerEvent): void {
    if (!this.whiteboardContainerElement || !this.gestureState || pointers.length !== 2) {
      return;
    }

    const distanceRatio = this.calculateDistanceRatio(pointers);
    const scaleChange = Math.abs(1 - distanceRatio);
    const centroid = this.getPointerCentroid(pointers);

    if (this.shouldStartZooming(scaleChange)) {
      this.gestureState.isZooming = true;
      this.gestureState.isPanning = false;
    }

    if (this.gestureState.isZooming) {
      this.handleZoom(pointers, distanceRatio, centroid, event);
    } else {
      this.handleTwoPointerPan(centroid, event);
    }
  }

  private calculateDistanceRatio(pointers: PointerPoint[]): number {
    if (!this.gestureState) {
      return 1;
    }
    const currentDistance = this.getPointerDistance(pointers);
    return this.gestureState.initialDistance > 0 ? currentDistance / this.gestureState.initialDistance : 1;
  }

  private shouldStartZooming(scaleChange: number): boolean {
    if (!this.gestureState) {
      return false;
    }
    return scaleChange > ZOOM_THRESHOLD && !this.gestureState.isZooming && !this.gestureState.isPanning;
  }

  private handleZoom(_pointers: PointerPoint[], distanceRatio: number, centroid: {x: number; y: number}, event: PointerEvent): void {
    if (!this.whiteboardContainerElement || !this.gestureState) {
      return;
    }

    const newScale = this.gestureState.initialScale * distanceRatio;
    const validNewScale = this.poster.scaling.validateNewZoom(newScale);
    const zoomCenter = this.calculateZoomCenter(centroid);
    const scrollPosition = this.calculateZoomScrollPosition(validNewScale, zoomCenter);

    const panDeltaX = centroid.x - this.gestureState.lastPanX;
    const panDeltaY = centroid.y - this.gestureState.lastPanY;

    this.handleMobilePageSelectionModeStateOnZooming();

    this.poster.scaling.setZoom(validNewScale, false);
    this.whiteboardContainerElement.scroll({
      left: scrollPosition.x - panDeltaX,
      top: scrollPosition.y - panDeltaY,
      behavior: 'instant',
    });
    this.poster.refreshAllVisibleCanvases();

    this.gestureState.currentGestureScale = validNewScale;
    this.gestureState.lastPanX = centroid.x;
    this.gestureState.lastPanY = centroid.y;
    event.preventDefault();
  }

  private calculateZoomCenter(centroid: {x: number; y: number}): {x: number; y: number} {
    if (!this.whiteboardContainerElement) {
      return {x: 0, y: 0};
    }
    const rect = this.whiteboardContainerElement.getBoundingClientRect();
    return {
      x: centroid.x - rect.left,
      y: centroid.y - rect.top,
    };
  }

  private calculateZoomScrollPosition(newScale: number, zoomCenter: {x: number; y: number}): {x: number; y: number} {
    if (!this.whiteboardContainerElement || !this.gestureState) {
      return {x: 0, y: 0};
    }
    const scaleRatio = newScale / this.gestureState.currentGestureScale;
    return {
      x: (this.whiteboardContainerElement.scrollLeft + zoomCenter.x) * scaleRatio - zoomCenter.x,
      y: (this.whiteboardContainerElement.scrollTop + zoomCenter.y) * scaleRatio - zoomCenter.y,
    };
  }

  private handleTwoPointerPan(centroid: {x: number; y: number}, event: PointerEvent): void {
    if (!this.whiteboardContainerElement || !this.gestureState) {
      return;
    }

    const panDeltaX = centroid.x - this.gestureState.lastPanX;
    const panDeltaY = centroid.y - this.gestureState.lastPanY;

    this.updateCumulativePanDelta(panDeltaX, panDeltaY);

    if (this.shouldStartPanning()) {
      this.startPanning();
    }

    if (this.gestureState.isPanning) {
      this.performPan(panDeltaX, panDeltaY);
      event.preventDefault();
    }

    this.gestureState.lastPanX = centroid.x;
    this.gestureState.lastPanY = centroid.y;
  }

  private getPointerDistance(pointers: PointerPoint[]): number {
    if (pointers.length !== 2) {
      return 0;
    }
    const dx = pointers[1].clientX - pointers[0].clientX;
    const dy = pointers[1].clientY - pointers[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getPointerCentroid(pointers: PointerPoint[]): {x: number; y: number} {
    const sumX = pointers.reduce((sum, pointer) => sum + pointer.clientX, 0);
    const sumY = pointers.reduce((sum, pointer) => sum + pointer.clientY, 0);
    return {
      x: sumX / pointers.length,
      y: sumY / pointers.length,
    };
  }

  private createInitialGestureState(): GestureState {
    return {
      pointers: new Map(),
      initialScale: 1,
      currentGestureScale: 1,
      initialDistance: 0,
      lastPanX: 0,
      lastPanY: 0,
      isPanning: false,
      isZooming: false,
      wasSelectableDisabled: false,
      cumulativePanDeltaX: 0,
      cumulativePanDeltaY: 0,
      isPageSwiping: false,
      isPointerDownOnSelectedItem: false,
    };
  }

  private scheduleRestoreSelectable(): void {
    this.restoreSelectableTimeoutId = window.setTimeout(() => {
      this.restoreFabricObjectsOriginalSelectable();
      this.restoreSelectableTimeoutId = undefined;
    }, RESTORE_SELECTABLE_DELAY_MS);
  }

  private handleZoomEnd(): void {
    this.trackPinchZoom();
    this.updateFitToScreenState();
    this.poster.redux.updateReduxData();

    this.handleMobilePageSelectionModeStateOnZoomEnd();

    if (this.shouldAutoFitToScreen()) {
      this.poster.scaling.fitToScreen(true);
    }
  }

  private trackPinchZoom(): void {
    if (!this.gestureState) {
      return;
    }
    const direction = getZoomDirection(this.gestureState.initialScale, this.poster.scaling.scale);
    if (direction) {
      trackCanvasZoom(CanvasZoomSource.PINCH, direction);
    }
  }

  private handleMobilePageSelectionModeStateOnZooming(): void {
    if (!isEditorMobileVariant()) {
      return;
    }

    if (!this.isZoomingOutFromFit()) {
      exitMobilePageSelectionMode();
      return;
    }

    transitionToMobilePageSelectionMode();
  }

  private handleMobilePageSelectionModeStateOnZoomEnd(): void {
    if (!isEditorMobileVariant()) {
      return;
    }

    if (!this.isZoomingOutFromFit()) {
      exitMobilePageSelectionMode();
      return;
    }

    if (this.poster.scaling.scale > this.poster.scaling.getFitToScreenScale() * 0.7) {
      exitMobilePageSelectionMode();
      return;
    }

    activateMobilePageSelectionMode();
  }

  private updateFitToScreenState(): void {
    const currentScale = this.poster.scaling.scale;
    this.poster.scaling.updateIsFitToScreenByScale(currentScale);
  }

  private shouldAutoFitToScreen(): boolean {
    const currentScale = this.poster.scaling.scale;
    const fitToScreenScale = this.poster.scaling.getFitToScreenScale();
    return currentScale < fitToScreenScale;
  }

  private isZoomingOutFromFit(): boolean {
    if (!this.gestureState) {
      return false;
    }

    const fitToScreenScale = this.poster.scaling.getFitToScreenScale();
    const wasInitiallyFitToScreenOrLess = this.gestureState.initialScale <= fitToScreenScale + 0.001;

    if (!wasInitiallyFitToScreenOrLess) {
      return false;
    }

    return this.poster.scaling.scale < this.gestureState.initialScale - 0.001;
  }
}
